import React from "react";
import PropTypes from "prop-types";
import { MountOnce } from "../../../../components/mixins";
import styled from "styled-components";
import { t } from "../../../../core/translate";

function generateCoverSizes(props) {
  const { isbn, coverWidths, coverURL, width } = props;
  const imgSrcTiny = generateCoverUrlWithIsbn(coverURL, isbn, coverWidths, 0);

  const imgSrc1x = generateCoverUrlWithIsbn(coverURL, isbn, coverWidths, width);

  const imgSrc2x = generateCoverUrlWithIsbn(
    coverURL,
    isbn,
    coverWidths,
    width * 2
  );

  return {
    imgSrc1x,
    imgSrc2x,
    imgSrcTiny
  };
}

function generateCoverUrl(base, possibleWidths, desiredWidth) {
  if (!possibleWidths || possibleWidths.length === 0) {
    return null;
  }

  let i = possibleWidths.findIndex(v => desiredWidth <= v);
  if (i === -1) {
    i = possibleWidths.length - 1;
  }
  return base.replace(":width", possibleWidths[i]);
}

function generateCoverUrlWithIsbn(base, isbn, possibleWidths, desiredWidth) {
  return generateCoverUrl(
    base.replace(":isbn", isbn),
    possibleWidths,
    desiredWidth
  );
}

const Container = styled.div`
  overflow: hidden;
  position: relative;
  ${props => `width: ${props.width}px`};
  ${props => (props.height ? `height: ${props.height}px` : "")};
  ${props => `border-radius: ${props.borderRadius}px`};

  img {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const BlurryImage = styled.img`
  width: 200%;
  height: 200%;
`;

const SimpleImage = styled.img`
  display: block;
  ${props => `border-radius: ${props.borderRadius}px`}
`;

/**
 * Generate a cover image with specific px dimensions, filling
 * in any extra space with a blurry version of the image
 */
export default class Cover extends React.Component {
  constructor(props) {
    super(props);
    this.state = generateCoverSizes(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.width !== nextProps.width ||
      this.props.isbn !== nextProps.isbn
    ) {
      this.setState({ ...generateCoverSizes(nextProps) });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.isbn !== nextProps.isbn ||
      this.props.width !== nextProps.width ||
      (nextProps.loadFullImage &&
        this.props.loadFullImage !== nextProps.loadFullImage) ||
      (nextProps.loadBlurredImage &&
        this.props.loadBlurredImage !== nextProps.loadBlurredImage)
    );
  }

  returnAltText() {
    if (this.props.altText === "") {
      return "";
    } else {
      return (
        this.props.altText ||
        t("library.continue-reading-with-title", "Continue Reading", {
          bookTitle: this.props.bookTitle
        })
      );
    }
  }

  render() {
    if (!this.state.imgSrc1x) {
      return null;
    }

    const imgProps = {
      src: this.state.imgSrc1x,
      srcSet: this.props.includeRetina ? `${this.state.imgSrc2x} 2x` : null
    };

    if (!this.props.height) {
      return (
        <SimpleImage
          {...imgProps}
          width={this.props.width}
          borderRadius={this.props.borderRadius}
          alt={this.returnAltText()}
        />
      );
    }

    return (
      <Container
        width={this.props.width}
        height={this.props.height}
        borderRadius={this.props.borderRadius}
      >
        <MountOnce reveal={this.props.loadBlurredImage}>
          <BlurryImage alt="" src={this.state.imgSrcTiny} aria-hidden />
        </MountOnce>
        <MountOnce reveal={this.props.loadFullImage}>
          <img
            {...imgProps}
            alt={this.returnAltText()}
            style={{
              maxWidth: this.props.width,
              maxHeight: this.props.height
            }}
          />
        </MountOnce>
      </Container>
    );
  }
}

Cover.defaultProps = {
  loadBlurredImage: true,
  loadFullImage: true,
  width: null,
  height: null,
  includeRetina: true,
  coverURL: `https:${Fraction.Environment.coversHost}/vbid/:isbn/width/:width?style=preview`,
  coverWidths: [
    20,
    25,
    28,
    30,
    32,
    35,
    60,
    120,
    150,
    200,
    240,
    320,
    480,
    640,
    720,
    768,
    960,
    1024
  ],
  borderRadius: 0
};

Cover.propTypes = {
  loadBlurredImage: PropTypes.bool,
  loadFullImage: PropTypes.bool,
  width: PropTypes.number.isRequired,
  height: PropTypes.number,
  isbn: PropTypes.string.isRequired,
  includeRetina: PropTypes.bool,
  borderRadius: PropTypes.number
};
